import React from 'react';

/** Tailwind's `md`. The width at which a three-column catalog stops fitting. */
const COMPACT_QUERY = '(max-width: 767px)';

/**
 * Whether the catalog should render in its compact form.
 *
 * A media **query**, not a `md:` class pair, because the two forms are
 * different structures rather than one structure restyled: on a phone the
 * seven areas are collapsed disclosures a reader opens one at a time, and on a
 * desktop they are all open with no toggle at all. Rendering both and hiding
 * one would put a second copy of every row in the accessibility tree, and give
 * the desktop a disclosure button that reports an `aria-expanded` it does not
 * control.
 *
 * Returns `false` where `matchMedia` is missing (jsdom, SSR): the desktop form
 * is the one with everything visible, which is the safer default for a test
 * that never sized a viewport.
 */
export const useCompactCatalog = (): boolean => {
    const [isCompact, setIsCompact] = React.useState(false);

    React.useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return;
        }

        const query = window.matchMedia(COMPACT_QUERY);
        setIsCompact(query.matches);

        const onChange = (event: MediaQueryListEvent) =>
            setIsCompact(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    return isCompact;
};
